import { API_URLS } from '@chessx/shared/api-routes'
import type {
  ReconciliationListQuery,
  ReconciliationListResponse,
  ReconciliationSource,
  ReconciliationSummary
} from '@chessx/shared/types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import axiosInstance from '../../network/axiosInstance'
import { toastMixin, toastErrorMixin } from '../../utils/interact'

export const RECONCILIATION_QUERY_KEY = 'RECONCILIATION'
export const RECONCILIATION_SUMMARY_QUERY_KEY = 'RECONCILIATION_SUMMARY'

const errorMessage = (error: unknown): string => {
  const response = (error as { response?: { data?: { error?: string } } })?.response
  return response?.data?.error ?? (error instanceof Error ? error.message : String(error))
}

export const useReconciliationList = (query: ReconciliationListQuery) =>
  useQuery({
    queryKey: [RECONCILIATION_QUERY_KEY, query],
    queryFn: async (): Promise<ReconciliationListResponse> => {
      const result = await axiosInstance.get(API_URLS.ADMIN_RECONCILIATION, { params: query })
      return result.data as ReconciliationListResponse
    },
    gcTime: 0
  })

export const useReconciliationSummary = () =>
  useQuery({
    queryKey: [RECONCILIATION_SUMMARY_QUERY_KEY],
    queryFn: async (): Promise<ReconciliationSummary> => {
      const result = await axiosInstance.get(API_URLS.ADMIN_RECONCILIATION_SUMMARY)
      return result.data as ReconciliationSummary
    },
    gcTime: 0
  })

export const useMarkReconciled = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (variables: { source: ReconciliationSource; id: string; note?: string }) => {
      const url = `${API_URLS.ADMIN_RECONCILIATION}/${variables.source}/${variables.id}/reconcile`
      await axiosInstance.put(url, { note: variables.note })
    },
    onSuccess: () => {
      toastMixin.fire({ title: 'Marked reconciled' })
      queryClient.invalidateQueries({ queryKey: [RECONCILIATION_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [RECONCILIATION_SUMMARY_QUERY_KEY] })
    },
    onError: (error: unknown) => {
      toastErrorMixin.fire({ title: errorMessage(error) })
    }
  })
}
